// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  createBrowserRouter,
  Navigate,
  Outlet,
  type RouteObject,
} from 'react-router';
import {
  Account,
  Accounts,
  AiBuilder,
  App,
  ClientProfile,
  Dashboard,
  Documents,
  IliquidAssets,
  NotFound,
  Portfolio,
  Positions,
  Rebalance,
  RebalanceSummary,
  Research,
  Trader,
  BankAccounts,
} from '@advisor-app/pages';
import { OAuthCallback } from '@shared/auth';
import { Layout } from '@advisor-app/features/ui/layout';
import {
  portfolioPositionsLoader,
  rebalanceConfigurationLoader,
  workflowLoader,
} from '@advisor-app/features/utils/workflow-loader';
import { clientInstitutionsLoader } from '@advisor-app/features/utils/client-institutions-loader';
import { AuthenticatedLayout } from '@shared/auth';
import {
  Summary,
  Loans,
  Cashflow,
  Transactions,
} from '@shared/advisory/aggregation/components';
import { Investment } from '@advisor-app/features/aggregation/investment';
import { Layout as AggregationLayout } from '@shared/advisory/aggregation/layouts';
import { InstitutionSyncProvider } from '@shared/advisory/aggregation/providers';
import { TagManagementProvider } from '@shared/tag-management';
type AppRouteObject =
  | false
  | RouteObject
  | (RouteObject & {
      children?: Array<AppRouteObject>;
    });

const cleanRoutes = (routes: Array<AppRouteObject>): RouteObject[] => {
  return routes
    .filter((route): route is Exclude<AppRouteObject, false> => Boolean(route))
    .map(route => {
      if (route.children) {
        const cleanedChildren = cleanRoutes(route.children);

        return {
          ...route,
          children: cleanedChildren.length ? cleanedChildren : undefined,
        };
      }

      return route;
    });
};

export const routes: Array<AppRouteObject> = [
  {
    path: '/',
    element: (
      <AuthenticatedLayout>
        <App />
        <InstitutionSyncProvider>
          <TagManagementProvider>
            <Layout />
          </TagManagementProvider>
        </InstitutionSyncProvider>
      </AuthenticatedLayout>
    ),
    errorElement: <NotFound error />,
    children: [
      {
        element: <Accounts />,
        index: true,
      },
      {
        element: <Dashboard />,
        path: 'dashboard',
      },
      {
        path: 'aggregation',
        element: (
          <AggregationLayout excludedPaths={['/aggregation/illiquids']}>
            <Outlet />
          </AggregationLayout>
        ),
        children: [
          {
            element: <Summary />,
            index: true,
          },
          {
            element: <Investment />,
            path: 'investment',
          },
          {
            element: <IliquidAssets />,
            path: 'illiquids',
          },
          {
            element: <Loans />,
            path: 'loans',
          },
          {
            element: <Cashflow />,
            path: 'cashflow',
          },
          {
            element: <Transactions />,
            path: 'transactions',
          },
        ],
      },
      {
        element: <Research />,
        path: 'research',
      },
      {
        element: <Outlet />,
        path: 'ai-builder',
        children: [
          {
            element: <Navigate to="stock" />,
            loader: workflowLoader('new', 'stock'),
            index: true,
          },
          ...(['stock', 'etf', 'mutual_fund'] as const).flatMap(asset => [
            {
              element: <AiBuilder workflow={asset.replace('_', '-') as any} />,
              path: asset.replace('_', '-'),
              loader: workflowLoader('new', asset),
            },
            {
              element: (
                <Positions
                  backTo={`/ai-builder/${asset.replace('_', '-')}` as any}
                />
              ),
              path: `${asset.replace('_', '-')}/positions`,
            },
          ]),
        ],
      },
      {
        element: <Outlet />,
        path: 'analysis',
        children: [
          {
            element: <Navigate to="stock" />,
            index: true,
            loader: workflowLoader('load', 'stock'),
          },
          ...(['stock', 'etf', 'mutual_fund'] as const).flatMap(asset => [
            {
              element: (
                <Portfolio
                  to={`/analysis/${asset.replace('_', '-')}/positions` as any}
                />
              ),
              path: asset.replace('_', '-'),
              loader: workflowLoader('load', asset),
            },
            {
              element: (
                <Positions backTo={`/analysis/${asset.replace('_', '-')}` as any} />
              ),
              loader: portfolioPositionsLoader(asset),
              path: `${asset.replace('_', '-')}/positions`,
            },
          ]),
        ],
      },
      {
        element: <Outlet />,
        path: 'rebalance',
        children: [
          {
            element: <Portfolio to="/rebalance/configuration" />,
            index: true,
            loader: workflowLoader('rebalance'),
          },
          {
            element: <Rebalance />,
            path: 'configuration',
            loader: rebalanceConfigurationLoader,
          },
          {
            element: <RebalanceSummary />,
            path: 'summary',
          },
        ],
      },
      {
        element: <Trader />,
        path: 'operations',
      },
      {
        element: <ClientProfile />,
        path: 'profile',
        loader: clientInstitutionsLoader,
      },
      {
        element: <Documents />,
        path: 'documents',
      },
      {
        element: <BankAccounts />,
        path: 'banks',
        loader: clientInstitutionsLoader,
      },
    ],
  },
  {
    path: 'account',
    errorElement: <NotFound error />,
    element: (
      <AuthenticatedLayout>
        <App />
        <Account />
      </AuthenticatedLayout>
    ),
  },
  {
    path: 'callback',
    element: <OAuthCallback redirectTo="/" />,
  },
  {
    path: '*',
    errorElement: <NotFound error />,
    element: <NotFound />,
  },
];

export const router = createBrowserRouter(cleanRoutes(routes));
